import { useEffect, useMemo, useRef } from 'react';

/** How long the pointer must rest on a row before it counts as meaning to open it. */
export const HOVER_INTENT_MS = 150;

/** A row's pointer handlers: `enter` when the pointer comes onto a person's row, `leave` when it goes. */
export interface HoverIntent {
  enter: (prospectId: string) => void;
  leave: () => void;
}

/**
 * Calls `onIntent` for a person once the pointer has rested on their row for `delayMs` — never for the rows a
 * pointer sweeps across on its way somewhere else. One timer for the whole list: coming onto another row, or
 * leaving, cancels the last. The handlers are the same every render, so memoized rows can take them.
 */
export function useHoverIntent(onIntent: ((prospectId: string) => void) | undefined, delayMs = HOVER_INTENT_MS): HoverIntent {
  const latest = useRef(onIntent);
  latest.current = onIntent;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  return useMemo(
    () => ({
      enter: (prospectId: string) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => latest.current?.(prospectId), delayMs);
      },
      leave: () => clearTimeout(timer.current),
    }),
    [delayMs],
  );
}
